import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@mui/material';
import { MdErrorOutline, MdRefresh } from 'react-icons/md';

interface Props {
  children: ReactNode;
  /** Changing this value resets the boundary (e.g. pass the route path). */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors in the subtree and shows the error instead
 * of letting React unmount the whole app to a blank white page. Without this,
 * any thrown error in a screen blanks the entire UI and the cause is only
 * visible in the browser console.
 *
 * Resets automatically when `resetKey` changes (we key it on the route path in
 * AppShell), so navigating away from a broken screen recovers cleanly.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the full error + component stack for diagnosis.
    console.error('[ErrorBoundary] Uncaught render error:', error, info);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-xl border border-border bg-surface p-6 text-center shadow-soft">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
            <MdErrorOutline size={26} />
          </div>
          <h2 className="text-[18px] font-semibold text-foreground">
            Something went wrong on this screen
          </h2>
          <p className="mt-1.5 text-[13.5px] text-foreground-muted">
            The rest of the app is fine — you can go back or reload. If this
            keeps happening, share the message below.
          </p>
          <pre className="mt-4 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-surface-muted px-3 py-2 text-left text-[12px] text-danger">
            {error.message || String(error)}
          </pre>
          <div className="mt-5 flex justify-center gap-2">
            <Button
              variant="outlined"
              size="small"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<MdRefresh />}
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
